<template>
  <v-card flat>
    <v-col cols="12" class="pa-0 px-3">
      <v-btn color="primary" icon @click="handleBack">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <span class="text-h6 mr-2" style="vertical-align: middle">
        {{ $t('message.pages.video.metricsPage.theVideoTraffic') }}
      </span>
    </v-col>
    <v-col cols="12" class="mb-1 px-5">
      <v-card flat>
        <span
          v-html="$t('message.pages.video.metricsPage.trafficMessage')"
        ></span>
      </v-card>
    </v-col>
    <v-col v-if="errors.fetch" cols="12">
      <v-row justify="center" class="mt-2">
        <span>{{
          $t('message.pages.video.metricsPage.videoTrafficError')
        }}</span>
      </v-row>
      <v-row class="mt-5" justify="center">
        <v-btn color="primary" @click="fetchData">{{
          $t('message.pages.newLive.tryAgain')
        }}</v-btn>
      </v-row>
    </v-col>
    <v-col v-else-if="loadings.fetch" cols="12" class="mt-5">
      <v-row align="center" justify="center">
        <loading-logo />
      </v-row>
    </v-col>
    <v-col cols="12" v-else-if="series[0].data.length > 0" class="px-0 pt-0">
      <apexchart
        :class="{ darkThemeSvgFix: $vuetify.theme.dark }"
        type="area"
        height="250"
        :options="chartOptions"
        :series="series"
      ></apexchart>
    </v-col>
    <v-col v-else cols="12">
      <v-row justify="center" class="mt-2">
        <span>{{
          $t('message.pages.video.metricsPage.videoTrafficError')
        }}</span>
      </v-row>
      <v-row class="mt-5" justify="center">
        <v-btn color="primary" @click="fetchData">{{
          $t('message.pages.newLive.tryAgain')
        }}</v-btn>
      </v-row>
    </v-col>
  </v-card>
</template>

<script>
import analyticsApi from '@/services/analyticsApi'
import { subDays, format, differenceInDays, addDays, addHours } from 'date-fns'
import { mapState } from 'vuex'
import i18n from '@/translations/lang'
import VueApexCharts from 'vue-apexcharts'
import calcMegabytesMixin from '@/utils/calcMegabytes.js'
import LoadingLogo from '../../../components/LoadingLogo.vue'

export default {
  name: 'VideoTraffic',
  components: {
    apexchart: VueApexCharts,
    LoadingLogo,
  },
  props: {
    customVideoId: {
      required: false,
    },
    videoTraffic: {
      required: false,
    },
    videoTrafficLabels: {
      required: false,
    },
    handleBack: {
      required: false,
    },
  },
  data() {
    let _this = this

    return {
      loadings: {
        fetch: true,
      },
      errors: {
        fetch: false,
      },
      series: [
        {
          name: i18n.t('message.trafficModal.consumeTraffic'),
          data: [],
        },
      ],
      estimatedTraffic: 0,
      chartOptions: {
        annotations: {
          xaxis: [],
        },
        chart: {
          toolbar: false,
          type: 'area',
          height: 350,
          zoom: {
            enabled: false,
          },
          offsetX: 0,
          offsetY: 0,
        },
        dataLabels: {
          enabled: false,
        },
        stroke: {
          curve: 'straight',
        },
        labels: [],
        xaxis: {
          type: 'numeric',
          tickAmount: 4,
          labels: {
            formatter(val) {
              return format(new Date(val), 'dd MMM')
            },
          },
        },
        yaxis: {
          labels: {
            formatter: function (value) {
              let maxValueUnit = _this.calcMegabytes(
                Math.max(..._this.series[0].data),
                'GB',
                'GB',
                true
              )

              if (maxValueUnit !== false) {
                maxValueUnit = maxValueUnit.split(' ')[1]
                return _this.calcMegabytes(value, 'GB', maxValueUnit)
              }
            },
          },
        },
        tooltip: {
          theme: 'dark',
        },
      },
    }
  },
  mixins: [calcMegabytesMixin],
  mounted() {
    this.$vuetify.theme.dark
      ? (this.chartOptions.tooltip.theme = 'dark')
      : (this.chartOptions.tooltip.theme = 'light')
    this.series[0].data = this.videoTraffic
    this.chartOptions.labels = this.videoTrafficLabels
    this.fetchData()
  },
  computed: {
    ...mapState('user', {
      planInfo: 'userPlanInfo',
    }),
  },
  methods: {
    async fetchData() {
      this.loadings.fetch = true
      try {
        const endOfGraph = format(new Date(), 'yyyy-MM-dd')
        const startOfGraph = format(
          subDays(new Date(this.planInfo.limit.start_period), 30),
          'yyyy-MM-dd'
        )

        const res = await analyticsApi.getVideoTrafficBandwidth(
          startOfGraph,
          endOfGraph,
          this.customVideoId ? this.customVideoId : this.$route.params.id
        )
        if (res.data.data.length === 0) {
          let daysDiff = differenceInDays(
            new Date(),
            subDays(new Date(this.planInfo.limit.start_period), 30)
          )
          let fakeCategoriesArray = []
          let fakeTrafficArray = []
          for (let i = 0; i <= daysDiff; i++) {
            fakeCategoriesArray.push(
              addDays(new Date(startOfGraph.replaceAll('-', '/')), i).getTime()
            )
            fakeTrafficArray.push(0)
          }
          this.series[0].data = fakeTrafficArray
          this.chartOptions.labels = fakeCategoriesArray
          this.errors.fetch = false
        } else if (res.data.data.length === 1) {
          const trafficArray = res.data.data.map((item) =>
            (item.b / (1024 * 1024 * 1024)).toFixed(1)
          )
          const categories = res.data.data.map((item) => {
            let date = new Date(item.t.replaceAll('-', '/'))
            return date.getTime()
          })

          for (let i = 0; i <= 10; i++) {
            let k = i
            trafficArray.push(0)
            categories.push(
              addHours(
                new Date(
                  res.data.data[res.data.data.length - 1].t.replaceAll('-', '/')
                ),
                ++k
              ).getTime()
            )
          }
          this.series[0].data = trafficArray
          this.chartOptions.labels = categories
          this.errors.fetch = false
        } else {
          const trafficArray = res.data.data.map((item) =>
            (item.b / (1024 * 1024 * 1024)).toFixed(1)
          )
          const categories = res.data.data.map((item) => {
            let date = new Date(item.t.replaceAll('-', '/'))
            return date.getTime()
          })
          this.series[0].data = trafficArray
          this.chartOptions.labels = categories
          this.errors.fetch = false
        }
      } catch (error) {
        this.errors.fetch = true
      }
      this.loadings.fetch = false
    },
  },
}
</script>
